import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import { z } from 'zod';

import EmailOtpDialog from '@shared/common/components/modals/EmailOtpDialog';
import { EmailRemoveConfirmDialog } from '@shared/common/components/modals/EmailRemoveConfirmDialog';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { useSendVerificationCodeQuery } from '@query/user';
import { useDeleteEmailMutation } from '@query/user';

import { CopyButton } from '@ui/uikit/components/common/CopyButton';
import { FormInput } from '@ui/uikit/components/common/FormInput';
import { Delete } from '@ui/uikit/components/icons/mono/Delete';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

const EMAIL_MAX_LENGTH = 254;

const EmailSchema = z.string().email();

type Props = {
  className?: string;
};

export const ProfileEmailField = ({ className }: Props) => {
  const { user } = useAuth();
  const [isVerifyEmailDialogOpen, setIsVerifyEmailDialogOpen] = useState(false);
  const [isRemoveEmailDialogOpen, setIsRemoveEmailDialogOpen] = useState(false);
  const emailFromMagicId = user?.attributes.email || '';

  const [email, setEmail] = useState(emailFromMagicId);
  const [verifyEmail, setVerifyEmail] = useState(email);

  const sendVerificationCodeQuery = useSendVerificationCodeQuery(verifyEmail, {
    enabled: !!verifyEmail,
  });

  const isEmailValid = EmailSchema.safeParse(email).success;

  const [isDirty, setIsDirty] = useState(false);

  const isEmailAlreadyInUseError =
    sendVerificationCodeQuery.isError &&
    getErrorMessage(sendVerificationCodeQuery.error).includes('already registered');

  const validationError = email && isDirty && !isEmailValid ? 'Email is invalid' : '';
  const verificationError = isEmailAlreadyInUseError && 'Email already in use';

  const isVerifyButtonDisabled =
    !email || !!validationError || email === emailFromMagicId || isEmailAlreadyInUseError;

  const { mutateAsync: deleteEmail, isPending: isDeleting } = useDeleteEmailMutation();

  useEffect(() => {
    setEmail(emailFromMagicId);
  }, [emailFromMagicId]);

  const handleEmailValueChange = (value: string) => {
    setEmail(value);
    setVerifyEmail('');
  };

  const handleOnDeleteEmailConfirm = async () => {
    setIsRemoveEmailDialogOpen(false);

    try {
      await deleteEmail();

      setEmail('');

      const text = `Email ${email}: successfully removed from MagicID`;

      toast.success(text);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleVerifyButtonClick = async () => {
    flushSync(() => {
      setVerifyEmail(email);
    });

    if (sendVerificationCodeQuery.data) {
      setIsVerifyEmailDialogOpen(true);
    } else {
      const { isError, error } = await sendVerificationCodeQuery.refetch();

      if (!isError || getErrorMessage(error).includes('confirmation code has already been sent')) {
        setIsVerifyEmailDialogOpen(true);
      }
    }
  };

  return (
    <div className={className}>
      <FormInput
        label="Email"
        type="email"
        data-qa="profile-email-input"
        onValueChange={handleEmailValueChange}
        value={email}
        onBlur={() => {
          if (email) {
            setIsDirty(true);
          }
        }}
        disabled={!!emailFromMagicId}
        hint={validationError || verificationError}
        isError={Boolean(validationError || verificationError)}
        // hide counter if email already set
        maxLength={emailFromMagicId ? undefined : EMAIL_MAX_LENGTH}
        afterInput={
          emailFromMagicId ? (
            <>
              <div className="flex gap-2">
                <button
                  data-skeleton-target="base"
                  type="button"
                  onClick={() => setIsRemoveEmailDialogOpen(true)}
                  className={classNames('msq-btn msq-btn-icon-lg msq-btn-secondary', {
                    'msq-btn-loading': isDeleting,
                  })}
                >
                  <Delete className="msq-btn-icon-child" />
                </button>

                <CopyButton
                  data-skeleton-target="base"
                  value={emailFromMagicId}
                  className="msq-btn msq-btn-icon-lg msq-btn-secondary"
                />
              </div>

              <EmailRemoveConfirmDialog
                open={isRemoveEmailDialogOpen}
                onOpenChange={setIsRemoveEmailDialogOpen}
                onConfirm={handleOnDeleteEmailConfirm}
              />
            </>
          ) : (
            <button
              data-skeleton-target="base"
              data-qa="profile-email-verify-button"
              type="button"
              disabled={isVerifyButtonDisabled}
              onClick={handleVerifyButtonClick}
              className={classNames('msq-btn msq-btn-icon-lg msq-btn-secondary min-w-[7.25rem]', {
                'msq-btn-loading': sendVerificationCodeQuery.isLoading,
              })}
            >
              Verify
            </button>
          )
        }
      />

      <EmailOtpDialog
        validEmail={verifyEmail}
        open={isVerifyEmailDialogOpen}
        onOpenChange={setIsVerifyEmailDialogOpen}
      />
    </div>
  );
};
